import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Mail, MapPin, CheckCircle, Building2, TrendingUp, Shield, Users, ChevronRight } from 'lucide-react';
import { useDynamicSEO } from '../hooks/useSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = ({ city, phone }) => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', property_type: '' });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          subject: `Landingpage ${city} – Anfrage`,
          message: `Objektart: ${form.property_type}\n\n${form.message}`
        })
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
    setSending(false);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
        <CheckCircle className="mx-auto text-green-500 mb-4" size={48} />
        <h3 className="text-xl font-semibold text-[#264F77] mb-2">Vielen Dank!</h3>
        <p className="text-gray-600">Wir melden uns innert 24 Stunden bei Ihnen.</p>
        <a href={`tel:${phone.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 mt-4 text-[#264F77] font-semibold hover:underline">
          <Phone size={16} /> Oder rufen Sie uns direkt an: {phone}
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 space-y-4" data-testid="landing-contact-form">
      <h3 className="text-xl font-semibold text-[#264F77] mb-2">Jetzt Immobilie bewerten lassen</h3>
      <p className="text-gray-500 text-sm mb-4">Kostenlos und unverbindlich – wir melden uns innert 24 Stunden.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input type="text" required placeholder="Vor- und Nachname *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none" />
        <input type="email" required placeholder="E-Mail-Adresse *" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none" />
        <input type="tel" placeholder="Telefonnummer" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none" />
        <select value={form.property_type} onChange={e => setForm({...form, property_type: e.target.value})} className="px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none text-gray-600">
          <option value="">Objektart wählen</option>
          <option value="Einfamilienhaus">Einfamilienhaus</option>
          <option value="Mehrfamilienhaus">Mehrfamilienhaus</option>
          <option value="Eigentumswohnung">Eigentumswohnung</option>
          <option value="Gewerbeobjekt">Gewerbeobjekt</option>
          <option value="Bauland">Bauland</option>
          <option value="Sonstiges">Sonstiges</option>
        </select>
      </div>
      <textarea placeholder="Ihre Nachricht (optional)" rows={3} value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none resize-none" />
      <button type="submit" disabled={sending} className="w-full py-4 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors shadow-lg disabled:opacity-50 flex items-center justify-center gap-2" data-testid="landing-submit-btn">
        {sending ? 'Wird gesendet...' : 'Jetzt Immobilie bewerten lassen'}
        {!sending && <ArrowRight size={18} />}
      </button>
      <p className="text-xs text-gray-400 text-center">Mit dem Absenden stimmen Sie unserer <Link to="/datenschutz" className="underline">Datenschutzerklärung</Link> zu.</p>
    </form>
  );
};

const LANDING_PAGES = {
  'immobilien-verkaufen-st-gallen': {
    h1: 'Immobilien verkaufen in St. Gallen',
    subtitle: 'Profitieren Sie von der professionellen Vermarktung Ihrer Liegenschaft in der Kantonshauptstadt.',
    city: 'St. Gallen',
    region: 'Kanton St. Gallen',
    metaTitle: 'Immobilien verkaufen in St. Gallen | LIVAG AG – Ihr lokaler Partner',
    metaDesc: 'Immobilie verkaufen in St. Gallen: Kostenlose Bewertung, diskrete Vermarktung und persönliche Beratung. LIVAG AG – Ihr Immobilienpartner in der Ostschweiz.',
    intro: 'St. Gallen ist eine der gefragtesten Immobilienlagen der Ostschweiz. Die hohe Lebensqualität, die Nähe zur Universität und die ausgezeichnete Verkehrsanbindung machen die Stadt zu einem attraktiven Standort für Eigentümer und Investoren. Ob Einfamilienhaus im Rosenberg, Mehrfamilienhaus in Bruggen oder Eigentumswohnung in der Altstadt – der richtige Vermarktungspartner macht den Unterschied.',
    whyUs: [
      'Fundierte Kenntnis des St. Galler Immobilienmarkts und der Quartiere',
      'Netzwerk aus qualifizierten Kaufinteressenten in der Region',
      'Diskrete und professionelle Vermarktung – auch off-market',
      'Kostenlose Erstbewertung und transparente Beratung'
    ],
    references: [
      { name: 'Eichenstrasse, St. Gallen', type: 'Mehrfamilienhaus-Entwicklung' },
      { name: 'St. Jakobstrasse, St. Gallen', type: 'Modernisierung Wohnliegenschaft' },
      { name: 'Rorschacherstrasse 182, St. Gallen', type: 'Projektentwicklung' }
    ],
    localInfo: 'Als Immobilienentwickler mit Sitz an der Katharinengasse 4 in St. Gallen kennen wir den lokalen Markt aus erster Hand. Wir wissen, welche Quartiere besonders nachgefragt sind, welche Preise realistisch erzielbar sind und wie wir Ihre Liegenschaft optimal positionieren.',
    relatedLinks: [
      { to: '/immobilien-kaufen-st-gallen', label: 'Immobilien kaufen in St. Gallen' },
      { to: '/projekte', label: 'Aktuelle Projekte' }
    ]
  },
  'immobilien-kaufen-st-gallen': {
    h1: 'Immobilien kaufen in St. Gallen',
    subtitle: 'Ihr Traumobjekt in der Ostschweizer Hauptstadt – mit LIVAG finden Sie die passende Immobilie.',
    city: 'St. Gallen',
    region: 'Kanton St. Gallen',
    metaTitle: 'Immobilien kaufen in St. Gallen | LIVAG AG – Neubauten & Bestandsobjekte',
    metaDesc: 'Haus oder Wohnung kaufen in St. Gallen: Neubauprojekte, Mehrfamilienhäuser und Eigentumswohnungen. LIVAG AG – Immobilienentwicklung Ostschweiz.',
    intro: 'Sie möchten in St. Gallen eine Immobilie kaufen? Ob als Eigenheim, Kapitalanlage oder Renditeobjekt – der St. Galler Immobilienmarkt bietet vielfältige Möglichkeiten. LIVAG AG entwickelt und vermittelt Liegenschaften in den attraktivsten Lagen der Stadt und Region.',
    whyUs: [
      'Eigene Neubauprojekte in St. Gallen und Umgebung',
      'Zugang zu exklusiven Objekten vor Marktveröffentlichung',
      'Beratung zu Finanzierung und Rendite',
      'Begleitung vom Erstgespräch bis zur Schlüsselübergabe'
    ],
    references: [
      { name: 'Eichenstrasse, St. Gallen', type: 'Mehrfamilienhaus-Entwicklung' },
      { name: 'Seebleiche', type: 'Neubauprojekt' },
      { name: 'Rorschacherstrasse 182', type: 'Projektentwicklung' }
    ],
    localInfo: 'LIVAG AG ist nicht nur Vermittler, sondern auch Entwickler. Wir realisieren eigene Projekte in St. Gallen – von der Standortanalyse über die Planung bis zur Fertigstellung. So haben unsere Kunden Zugang zu hochwertigen Neubauten, bevor diese öffentlich angeboten werden.',
    relatedLinks: [
      { to: '/immobilien-verkaufen-st-gallen', label: 'Immobilie verkaufen in St. Gallen' },
      { to: '/projekte', label: 'Aktuelle Projekte' }
    ]
  },
  'immobilienmakler-gossau': {
    h1: 'Immobilienmakler in Gossau SG',
    subtitle: 'LIVAG AG – Ihr Immobilienpartner für Gossau und das Fürstenland.',
    city: 'Gossau',
    region: 'Gossau SG / Fürstenland',
    metaTitle: 'Immobilienmakler Gossau SG | LIVAG AG – Bewertung & Verkauf',
    metaDesc: 'Immobilienmakler in Gossau SG: Professionelle Bewertung, Vermarktung und Verkauf Ihrer Liegenschaft. LIVAG AG – Ihr Partner für Immobilien im Fürstenland.',
    intro: 'Gossau im Fürstenland wächst stetig und bietet als gut angebundener Standort zwischen St. Gallen und Wil attraktive Möglichkeiten für Immobilieneigentümer und Käufer. LIVAG AG begleitet Sie bei Kauf, Verkauf und Entwicklung von Liegenschaften in Gossau und Umgebung.',
    whyUs: [
      'Lokale Marktkenntnisse in Gossau und Umgebung',
      'Persönliche Betreuung durch erfahrene Immobilienspezialisten',
      'Professionelle Bewertung und diskrete Vermarktung',
      'Entwicklung und Vermittlung in der gesamten Region'
    ],
    references: [],
    localInfo: 'Gossau gehört zu den am stärksten wachsenden Gemeinden im Kanton St. Gallen. Die gute Anbindung an die Autobahn A1 und die Nähe zu St. Gallen machen die Stadt zu einem gefragten Wohnort. LIVAG AG kennt die lokalen Gegebenheiten und unterstützt Sie bei allen Immobilienthemen.',
    relatedLinks: [
      { to: '/haus-kaufen-gossau', label: 'Haus kaufen in Gossau' },
      { to: '/immobilien-verkaufen-st-gallen', label: 'Immobilien verkaufen in St. Gallen' }
    ]
  },
  'haus-kaufen-gossau': {
    h1: 'Haus kaufen in Gossau SG',
    subtitle: 'Einfamilienhäuser, Doppelhaushälften und Neubauten in Gossau und Umgebung.',
    city: 'Gossau',
    region: 'Gossau SG / Fürstenland',
    metaTitle: 'Haus kaufen in Gossau SG | LIVAG AG – Einfamilienhäuser & Neubauten',
    metaDesc: 'Haus kaufen in Gossau SG: Einfamilienhäuser, Neubauten und Renditeobjekte. LIVAG AG berät Sie persönlich bei Ihrem Immobilienkauf im Fürstenland.',
    intro: 'Sie suchen ein Eigenheim in Gossau? Das Fürstenland bietet ruhige Wohnlagen mit hervorragender Infrastruktur. LIVAG AG bietet Ihnen Zugang zu attraktiven Objekten – vom modernen Neubau bis zum charmanten Bestandshaus.',
    whyUs: [
      'Zugang zu Neubauprojekten und Bestandsobjekten in Gossau',
      'Professionelle Beratung zu Finanzierung und Kaufprozess',
      'Erfahrung mit verschiedenen Objektarten (EFH, MFH, Bauland)',
      'Begleitung durch den gesamten Kaufprozess'
    ],
    references: [],
    localInfo: 'Gossau bietet ein attraktives Preis-Leistungs-Verhältnis im Vergleich zur Stadt St. Gallen. Gleichzeitig profitiert man von kurzen Wegen und einer guten öffentlichen Verkehrsanbindung. LIVAG AG kennt die verschiedenen Quartiere und berät Sie bei der Wahl des richtigen Standorts.',
    relatedLinks: [
      { to: '/immobilienmakler-gossau', label: 'Immobilienmakler in Gossau' },
      { to: '/projekte', label: 'Aktuelle Projekte' }
    ]
  },
  'immobilienmakler-wil-sg': {
    h1: 'Immobilienmakler in Wil SG',
    subtitle: 'LIVAG AG – Immobilienberatung und -vermittlung in Wil und Region.',
    city: 'Wil',
    region: 'Wil SG',
    metaTitle: 'Immobilienmakler Wil SG | LIVAG AG – Immobilienbewertung & Verkauf',
    metaDesc: 'Immobilienmakler in Wil SG: Bewertung, Vermarktung und Verkauf Ihrer Liegenschaft. LIVAG AG – Ihr Partner für Immobilien in Wil und der Ostschweiz.',
    intro: 'Wil ist eine der dynamischsten Städte der Ostschweiz. Gelegen zwischen St. Gallen und Winterthur, verbindet Wil historischen Charme mit moderner Infrastruktur. LIVAG AG unterstützt Eigentümer und Käufer mit professioneller Immobilienberatung in der Region Wil.',
    whyUs: [
      'Erfahrung in der Immobilienentwicklung in der Region Wil',
      'Diskrete Vermarktung und professionelle Verhandlungsführung',
      'Netzwerk aus lokalen Partnern und Kaufinteressenten',
      'Kostenlose Erstberatung ohne Verpflichtung'
    ],
    references: [],
    localInfo: 'Wil profitiert von seiner strategischen Lage an der Achse St. Gallen–Zürich. Die historische Altstadt, gute Schulen und ein vielfältiges Freizeitangebot machen Wil zu einem beliebten Wohnort für Familien und Pendler.',
    relatedLinks: [
      { to: '/immobilien-wil-sg', label: 'Immobilien in Wil SG' },
      { to: '/immobilien-verkaufen-st-gallen', label: 'Immobilien verkaufen in St. Gallen' }
    ]
  },
  'immobilien-wil-sg': {
    h1: 'Immobilien in Wil SG',
    subtitle: 'Kaufen, verkaufen und entwickeln – Ihr Immobilienpartner in Wil.',
    city: 'Wil',
    region: 'Wil SG',
    metaTitle: 'Immobilien Wil SG – Kaufen & Verkaufen | LIVAG AG',
    metaDesc: 'Immobilien in Wil SG: Häuser, Wohnungen und Mehrfamilienhäuser kaufen oder verkaufen. LIVAG AG – Immobilienentwicklung und Vermittlung Ostschweiz.',
    intro: 'Der Immobilienmarkt in Wil und Umgebung bietet attraktive Chancen für Käufer und Verkäufer. LIVAG AG entwickelt und vermittelt Liegenschaften in der Region – von Einfamilienhäusern über Eigentumswohnungen bis zu Renditeobjekten.',
    whyUs: [
      'Eigene Neubauprojekte und Entwicklungen in der Region',
      'Zugang zu Off-Market-Objekten',
      'Beratung für Investoren und Eigennutzer',
      'Langjährige Erfahrung im Ostschweizer Immobilienmarkt'
    ],
    references: [],
    localInfo: 'Die Region Wil wächst. Mit der guten Verkehrsanbindung via A1 und Bahn sowie der Nähe zu Zürich und St. Gallen ist Wil ein Standort mit Perspektive – für Eigenheimbesitzer und Investoren gleichermassen.',
    relatedLinks: [
      { to: '/immobilienmakler-wil-sg', label: 'Immobilienmakler in Wil' },
      { to: '/projekte', label: 'Aktuelle Projekte' }
    ]
  },
  'immobilienmakler-herisau': {
    h1: 'Immobilienmakler in Herisau',
    subtitle: 'LIVAG AG – Immobilienberatung in Herisau und Appenzell Ausserrhoden.',
    city: 'Herisau',
    region: 'Appenzell Ausserrhoden',
    metaTitle: 'Immobilienmakler Herisau | LIVAG AG – Bewertung & Vermittlung',
    metaDesc: 'Immobilienmakler in Herisau: Professionelle Immobilienbewertung und Vermarktung in Appenzell Ausserrhoden. LIVAG AG – Ihr lokaler Partner.',
    intro: 'Herisau als Hauptort von Appenzell Ausserrhoden bietet eine einzigartige Kombination aus ländlichem Charme und guter Infrastruktur. LIVAG AG ist Ihr Ansprechpartner für Immobilienentwicklung, Bewertung und Vermittlung in der Region.',
    whyUs: [
      'Erfahrung mit Projekten in Herisau und Appenzell Ausserrhoden',
      'Regionale Marktkenntnisse und lokale Vernetzung',
      'Professionelle Immobilienbewertung',
      'Diskrete und persönliche Betreuung'
    ],
    references: [
      { name: 'Zwei MFH Herisau', type: 'Mehrfamilienhaus-Neubau' }
    ],
    localInfo: 'LIVAG AG hat bereits erfolgreich Mehrfamilienhäuser in Herisau realisiert. Wir kennen den lokalen Markt, die Baubewilligungsprozesse und die Bedürfnisse der Bewohner in der Region.',
    relatedLinks: [
      { to: '/immobilien-verkaufen-st-gallen', label: 'Immobilien verkaufen in St. Gallen' },
      { to: '/projekte', label: 'Referenzprojekte' }
    ]
  },
  'immobilienmakler-rorschach': {
    h1: 'Immobilienmakler in Rorschach',
    subtitle: 'LIVAG AG – Ihr Immobilienpartner am Bodensee.',
    city: 'Rorschach',
    region: 'Rorschach / Bodenseeregion',
    metaTitle: 'Immobilienmakler Rorschach | LIVAG AG – Immobilien am Bodensee',
    metaDesc: 'Immobilienmakler in Rorschach: Bewertung und Verkauf von Immobilien am Bodensee. LIVAG AG – Immobilienentwicklung und Vermittlung Ostschweiz.',
    intro: 'Die Bodenseeregion gehört zu den attraktivsten Wohnlagen der Ostschweiz. Rorschach und seine Nachbargemeinden bieten eine hohe Lebensqualität direkt am See. LIVAG AG begleitet Sie bei Kauf, Verkauf und Entwicklung von Immobilien in der Region.',
    whyUs: [
      'Erfahrung in der Bodenseeregion',
      'Kenntnis der besonderen Anforderungen am Seeufer',
      'Breites Netzwerk von Kaufinteressenten',
      'Professionelle Vermarktung mit Fokus auf die Lagequalität'
    ],
    references: [],
    localInfo: 'Rorschach und die umliegenden Gemeinden wie Rorschacherberg und Goldach profitieren von der einmaligen Lage am Bodensee. Die Kombination aus See, Bergen und guter Verkehrsanbindung macht die Region zu einem Premium-Standort. LIVAG AG kennt die lokalen Gegebenheiten und berät Sie individuell.',
    relatedLinks: [
      { to: '/immobilien-verkaufen-st-gallen', label: 'Immobilien verkaufen in St. Gallen' },
      { to: '/projekte', label: 'Referenzprojekte' }
    ]
  }
};

const PHONE = '071 226 11 99';

export const LocalLanding = () => {
  const path = window.location.pathname.replace(/^\//, '');
  const page = LANDING_PAGES[path];

  useDynamicSEO({
    title: page?.metaTitle || 'Immobilien Ostschweiz | LIVAG AG',
    description: page?.metaDesc || '',
    canonical: page ? `https://www.livag.ch/${path}` : 'https://www.livag.ch',
    ogImage: null
  });

  if (!page) return null;

  return (
    <div className="min-h-screen bg-white" data-testid={`landing-${path}`}>
      {/* JSON-LD LocalBusiness + Service */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "RealEstateAgent",
        "name": "LIVAG AG",
        "description": page.metaDesc,
        "url": `https://www.livag.ch/${path}`,
        "telephone": "+41 71 226 11 99",
        "address": { "@type": "PostalAddress", "streetAddress": "Katharinengasse 4", "addressLocality": "St. Gallen", "postalCode": "9000", "addressCountry": "CH" },
        "areaServed": { "@type": "City", "name": page.city }
      }) }} />

      {/* Breadcrumb Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://www.livag.ch/" },
          { "@type": "ListItem", "position": 2, "name": page.h1, "item": `https://www.livag.ch/${path}` }
        ]
      }) }} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#264F77] via-[#1e3d5c] to-[#162d42] text-white">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTTAgMCBMNjAgNjBNNjAgMCBMMCA2MCIgc3Ryb2tlPSIjZmZmIiBzdHJva2Utd2lkdGg9IjAuMyIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IGZpbGw9InVybCgjZykiIHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiLz48L3N2Zz4=')] opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          {/* Breadcrumb */}
          <nav className="mb-8 flex items-center text-sm text-white/60" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <ChevronRight size={14} className="mx-2" />
            <span className="text-white/90">{page.h1}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-sm font-medium mb-6">
                <MapPin size={14} />
                {page.region}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-normal leading-tight mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                {page.h1}
              </h1>
              <p className="text-lg text-white/80 mb-8 leading-relaxed max-w-xl">
                {page.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#264F77] font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg">
                  <Phone size={18} />
                  {PHONE}
                </a>
                <a href="mailto:info@livag.ch" className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/20">
                  <Mail size={18} />
                  info@livag.ch
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:mt-0 mt-4">
              <ContactForm city={page.city} phone={PHONE} />
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Content Column */}
            <article className="lg:col-span-2 space-y-12">
              {/* Intro */}
              <div>
                <h2 className="text-2xl sm:text-3xl font-semibold text-[#264F77] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {page.city} – Immobilienstandort mit Potenzial
                </h2>
                <p className="text-gray-600 leading-relaxed text-lg">{page.intro}</p>
              </div>

              {/* Why LIVAG */}
              <div>
                <h2 className="text-2xl font-semibold text-[#264F77] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Warum LIVAG AG in {page.city}?
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {page.whyUs.map((point, i) => (
                    <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                      <CheckCircle className="text-[#264F77] mt-0.5 flex-shrink-0" size={20} />
                      <p className="text-gray-700">{point}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Local Knowledge */}
              <div className="bg-[#264F77]/5 rounded-2xl p-8">
                <h2 className="text-2xl font-semibold text-[#264F77] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Lokale Kompetenz
                </h2>
                <p className="text-gray-600 leading-relaxed">{page.localInfo}</p>
              </div>

              {/* References */}
              {page.references.length > 0 && (
                <div>
                  <h2 className="text-2xl font-semibold text-[#264F77] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Unsere Referenzen in {page.city}
                  </h2>
                  <div className="space-y-3">
                    {page.references.map((ref, i) => (
                      <Link key={i} to="/projekte" className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors group">
                        <div className="flex items-center gap-3">
                          <Building2 className="text-[#264F77]" size={20} />
                          <div>
                            <p className="font-semibold text-gray-900">{ref.name}</p>
                            <p className="text-sm text-gray-500">{ref.type}</p>
                          </div>
                        </div>
                        <ArrowRight className="text-gray-400 group-hover:text-[#264F77] group-hover:translate-x-1 transition-all" size={18} />
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>

            {/* Sidebar */}
            <aside className="space-y-6">
              {/* Services */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-[#264F77] mb-4">Unsere Leistungen</h3>
                <div className="space-y-3">
                  {[
                    { icon: TrendingUp, label: 'Immobilienbewertung', desc: 'Kostenlos und marktgerecht' },
                    { icon: Shield, label: 'Diskrete Vermarktung', desc: 'Off-Market und öffentlich' },
                    { icon: Building2, label: 'Projektentwicklung', desc: 'Vom Bauland zum Schlüssel' },
                    { icon: Users, label: 'Persönliche Beratung', desc: 'Ihr Ansprechpartner vor Ort' }
                  ].map(({ icon: Icon, label, desc }, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Icon className="text-[#264F77] mt-0.5" size={18} />
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{label}</p>
                        <p className="text-xs text-gray-500">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-[#264F77] text-white rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4">Direkter Kontakt</h3>
                <div className="space-y-3">
                  <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="flex items-center gap-3 text-white/90 hover:text-white transition-colors">
                    <Phone size={16} /> {PHONE}
                  </a>
                  <a href="mailto:info@livag.ch" className="flex items-center gap-3 text-white/90 hover:text-white transition-colors">
                    <Mail size={16} /> info@livag.ch
                  </a>
                  <div className="flex items-start gap-3 text-white/90">
                    <MapPin size={16} className="mt-0.5" />
                    <div>
                      <p>Katharinengasse 4</p>
                      <p>9000 St. Gallen</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Related Links */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-[#264F77] mb-4">Weiterführende Seiten</h3>
                <div className="space-y-2">
                  {page.relatedLinks.map(({ to, label }, i) => (
                    <Link key={i} to={to} className="flex items-center gap-2 text-gray-600 hover:text-[#264F77] transition-colors text-sm py-1">
                      <ChevronRight size={14} /> {label}
                    </Link>
                  ))}
                  <Link to="/blog" className="flex items-center gap-2 text-gray-600 hover:text-[#264F77] transition-colors text-sm py-1">
                    <ChevronRight size={14} /> Blog & Ratgeber
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 bg-[#264F77]">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-normal mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Bereit für den nächsten Schritt?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Lassen Sie Ihre Immobilie kostenlos bewerten oder vereinbaren Sie ein unverbindliches Erstgespräch mit unseren Experten.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${PHONE.replace(/\s/g, '')}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#264F77] font-semibold rounded-lg hover:bg-gray-100 transition-colors shadow-lg">
              <Phone size={18} />
              Jetzt anrufen: {PHONE}
            </a>
            <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 text-white font-semibold rounded-lg hover:bg-white/20 transition-colors border border-white/30">
              Kostenloses Erstgespräch vereinbaren
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export const LANDING_PAGE_SLUGS = Object.keys(LANDING_PAGES);
